<template>
    <page-layout :readonly="isProposalAccepted" :hide-footer="isProposalAccepted">
        <template #header>
            <proposal-step-header
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :title="pageTitle"
                :use-exit-modal="!isProposalAccepted && exitConfirmationRequired"
                :is-loading="isLoading"
            />
        </template>
        <template #default>
            <g-loader v-if="isLoading" type="fixed" />
            <template v-else>
                <out-of-stock
                    v-if="shouldHandleOutOfStock && allProductsOutOfStock"
                    :custom-message="customOutOfStockMessage"
                />
                <template v-else>
                    <div class="margin-bottom">
                        <g-typography tag="p" class="break-new-line">
                            {{ stepOverview }}
                        </g-typography>
                    </div>
                    <template v-if="useCheckoutUrl">
                        <g-collapsible-card
                            title="Instructions"
                            :active="true"
                            :show-arrow="false"
                            class="margin-bottom-large"
                        >
                            <g-typography tag="p">
                                1. Go to our website:
                                <a :href="storeUrl" target="_blank">{{ storeUrl }}</a>
                            </g-typography>
                            <div v-if="selectionMethodWebsiteShopifySharedCart">
                                <g-typography tag="p"> 2. Browse our catalog and add the items you’d like to receive to your cart. </g-typography>
                                <g-typography tag="p"> 3. Go to the cart page. </g-typography>
                                <g-typography tag="p"> 4. Click the Share Cart button to copy the URL, and paste it below. </g-typography>
                                <g-typography tag="p" color="secondary" class="example-link">
                                    <div>The link should look similar to this:</div>
                                    <div>{{ storeUrl }}/shared-cart/197659668480:1,196142759117:2</div>
                                </g-typography>
                            </div>
                            <div v-else-if="selectionMethodWebsiteShopify">
                                <g-typography tag="p"> 2. Browse our catalog and click an item you want. </g-typography>
                                <g-typography tag="p"> 3. Copy the product URL and paste it below. </g-typography>
                                <g-typography tag="p"> 4. Click Add Product. </g-typography>
                                <g-typography tag="p">
                                    5. Redo this for any other product you wish to add.
                                </g-typography>
                                <g-typography tag="p" color="secondary" class="example-link">
                                    <div>The link should look similar to this:</div>
                                    <div>{{ storeUrl }}/Product-name</div>
                                </g-typography>
                            </div>
                            <div v-else>
                                <g-typography tag="p">
                                    2. Browse our catalog and add the items you’d like to receive to your cart.
                                </g-typography>
                                <g-typography tag="p">
                                    3. Go to the first step of checkout, copy the URL, and paste it below.
                                </g-typography>
                                <g-typography tag="p" color="secondary" class="example-link">
                                    <div>The link should look similar to this:</div>
                                    <div>{{ storeUrl }}/254781/checkouts/bc2b897c2</div>
                                </g-typography>
                            </div>
                        </g-collapsible-card>
                        <div class="margin-bottom-large">
                            <g-form>
                                <g-form-item :errors="checkoutError">
                                    <g-input-search
                                        v-if="selectionMethodWebsiteShopify"
                                        v-model="checkoutUrl"
                                        placeholder="Store Product URL"
                                        enter-button="Add Product"
                                        :loading="isLoadingCheckoutUrl"
                                        @search="handleShopifyProductUrl"
                                    />
                                    <g-input-search
                                        v-else
                                        v-model="checkoutUrl"
                                        placeholder="Checkout URL"
                                        enter-button="Confirm"
                                        :loading="isLoadingCheckoutUrl"
                                        @search="handleCheckoutUrl"
                                    />
                                </g-form-item>
                            </g-form>
                        </div>
                    </template>
                    <div v-if="selectProducts" class="margin-bottom">
                        <g-level>
                            <g-level-item>
                                <g-typography :color="countLevelColor">
                                    {{ selectedProductsString }}
                                </g-typography>
                            </g-level-item>
                            <template #right>
                                <g-typography :color="priceLevelColor">
                                    {{ remainingFundsString }}
                                </g-typography>
                            </template>
                        </g-level>
                    </div>
                    <error-message-card
                        v-if="isCountLimitExceeded || isPriceLimitExceeded"
                        class="margin-bottom"
                        message="You've reached a limit. Please remove some items to continue."
                    />
                    <proposal-step-error
                        v-else-if="proposalStep && proposalStep.error"
                        class="margin-bottom-normal"
                        :step="proposalStep"
                    />
                    <error-message-card v-else-if="error" class="margin-bottom-normal" :message="error" />
                    <div v-if="maxProductsCount === 0 && selectProducts && !useCheckoutUrl" class="margin-bottom">
                        <proposal-product-select-card @click="() => handleSelectProductClick()" />
                    </div>
                    <template v-for="(type, index) in renderCardTypes">
                        <div v-if="type === restrictionProductCardType" class="margin-bottom">
                            <proposal-product-select-card
                                @click="() => handleSelectProductClick(productRestrictions[index], index)"
                            />
                        </div>
                        <template v-else>
                            <selected-product-card
                                v-if="!isHidden(products[index])"
                                :product="products[index]"
                                :campaign="campaign"
                                :preselected-product="preselectedProducts[index]"
                                class="margin-bottom"
                                @edit="(variantId) => handleEditProductClick(products[index], index, variantId)"
                                @remove="() => handleRemoveProduct(index)"
                            />
                        </template>
                    </template>
                </template>
            </template>
        </template>
        <template #footer>
            <proposal-step-footer
                :is-next-disabled="isNextDisabled"
                :loading="isCompletingProposal"
                :step-completed="isCompleted"
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :use-exit-modal="!isProposalAccepted && exitConfirmationRequired"
                @go-next-step="$emit('go-next-step', proposalStep, payload)"
                @go-previous-step="$emit('go-previous-step', proposalStep)"
            />
        </template>
    </page-layout>
</template>

<script>
    import * as _ from 'lodash';
    import PageLayout from '../../layouts/PageLayout';
    import ProposalStepHeader from './partials/ProposalStepHeader';
    import ProposalStepFooter from './partials/ProposalStepFooter';
    import ProposalProductSelectCard from './partials/ProposalProductSelectCard';
    import SelectedProductCard from './partials/SelectedProductCard';
    import ProductsMixin from './mixins/ProductsMixin';
    import ProposalMixin from './mixins/ProposalMixin';
    import { formatCurrency } from '../../../helpers/formatters';
    import ErrorMessageCard from './partials/ErrorMessageCard';
    import ProposalStepError from './partials/PoposalStepError';
    import { getCustomizableTitle, getProposalStepOverview } from '../../../helpers/default-texts';
    import { LIVE_CAMPAIGN_PROPOSAL_PRODUCT_SELECTION_START_EVENT } from '../../../constants/analytics-events';
    import OutOfStock from '../../library/OutOfStock';
    import { useFeatureFlagsStore } from '../../../../stores';

    const PRODUCT_CARD_TYPE_RESTRICTION = 'restriction';
    const PRODUCT_CARD_TYPE_SELECTED = 'selected';

    export default {
        name: 'ProposalProductsView',
        mixins: [ProductsMixin, ProposalMixin],
        components: {
            OutOfStock,
            ErrorMessageCard,
            SelectedProductCard,
            PageLayout,
            ProposalStepHeader,
            ProposalStepFooter,
            ProposalProductSelectCard,
            ProposalStepError
        },
        props: {
            proposalStep: {
                type: Object,
                default: null
            },
            campaign: {
                type: Object,
                default: null
            },
            products: {
                type: Array,
                default: () => {
                    return [];
                }
            },
            preselectedProducts: {
                type: Array
            },
            isLoadingCheckoutUrl: {
                type: Boolean,
                default: false
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isCompletingProposal: {
                type: Boolean,
                default: false
            },
            errorMessage: {
                type: String,
                default: null
            },
            errors: {
                type: [Array, Object],
                default: () => {
                    return [];
                }
            },
            checkOutUrlErrorMessage: {
                type: String
            },
            exitConfirmationRequired: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                price: '',
                checkoutUrl: '',
                restrictionProductCardType: PRODUCT_CARD_TYPE_RESTRICTION
            };
        },
        computed: {
            pageTitle() {
                return getCustomizableTitle(this.campaign, 'select_products_step_title');
            },
            stepOverview() {
                let defaultText = "Here are the products you'll receive.";
                if (this.useCheckoutUrl) {
                    defaultText = "Head to our website and select the products you'd like to receive.";
                } else if (this.hasProductsWithOptions || this.selectProducts) {
                    defaultText = 'Select the products you would like to receive.';
                }

                return getProposalStepOverview(this.campaign, 'select_products_step_overview', defaultText);
            },
            stepNumber() {
                return _.get(this, 'proposalStep.meta.stepNumber', 0);
            },
            totalSteps() {
                return _.get(this, 'proposalStep.meta.totalSteps', 0);
            },
            useCheckoutUrl() {
                return _.get(this.campaign, 'compensation_settings.use_checkout_url', false);
            },
            selectionMethodWebsiteShopifySharedCart() {
                const isEnabled = useFeatureFlagsStore().data['product-select-from-website-q2-2024'] || false;
                const ecommerceAccountProvider = _.get(this, 'campaign.brand.ecommerce_account.provider');

                return this.useCheckoutUrl && ecommerceAccountProvider === 'shopify' && isEnabled;
            },
            selectionMethodWebsiteShopify() {
                if (this.selectionMethodWebsiteShopifySharedCart) {
                    return false;
                }
                const isEnabled = useFeatureFlagsStore().data['campaigns-new-product-select-from-website'] || false;
                const ecommerceAccountProvider = _.get(this, 'campaign.brand.ecommerce_account.provider');
                return this.useCheckoutUrl && ecommerceAccountProvider === 'shopify' && isEnabled;
            },
            shouldHandleOutOfStock() {
                return !this.selectProducts && !this.isProposalAccepted;
            },
            maxProductsPrice() {
                return _.get(this.campaign, 'compensation_settings.max_product_price', 0);
            },
            productsPrice() {
                return this.products.reduce((sum, product) => {
                    let price;

                    if (_.get(product, 'variant.price')) {
                        price = product.variant.price || 0;
                    } else {
                        price = _.get(product, 'price', 0);
                    }

                    sum = sum + price;
                    return sum;
                }, 0);
            },
            remainingFunds() {
                const remainingFunds = this.maxProductsPrice - this.productsPrice;

                if (remainingFunds < 1 && remainingFunds > 0) {
                    return 0;
                }

                return remainingFunds;
            },

            remainingFundsString() {
                const formattedPrice = formatCurrency(this.remainingFunds, _.get(this.campaign, 'brand.currency', ''));

                return this.maxProductsPrice && this.maxProductsPrice !== 0 ? `Remaining Funds: ${formattedPrice}` : '';
            },
            showPrices() {
                return _.get(this.campaign, 'compensation_settings.show_prices', true);
            },
            storeUrl() {
                return _.get(this.campaign, 'brand.website_url', '');
            },
            isNextDisabled() {
                return (
                    this.isCountLimitExceeded ||
                    this.isPriceLimitExceeded ||
                    this.isIncompleteSelection ||
                    (this.hasOutOfStockProducts && this.useCheckoutUrl)
                );
            },
            isIncompleteSelection() {
                return this.products
                    .filter((product) => !this.isHidden(product))
                    .some((product) => {
                        return product.select_options && product.variants.length > 0 && product.variant_id === null;
                    });
            },
            isCountLimitExceeded() {
                return (
                    this.selectProducts &&
                    this.maxProductsCount &&
                    this.maxProductsCount > 0 &&
                    this.selectedProductsCount > this.maxProductsCount
                );
            },
            isPriceLimitExceeded() {
                return (
                    this.selectProducts &&
                    this.maxProductsPrice &&
                    this.maxProductsPrice > 0 &&
                    this.productsPrice > this.maxProductsPrice
                );
            },
            countLevelColor() {
                return this.isCountLimitExceeded ? 'error' : 'secondary';
            },
            priceLevelColor() {
                return this.isPriceLimitExceeded ? 'error' : 'secondary';
            },
            hasOutOfStockProducts() {
                if (this.products.length < 1) {
                    return false;
                }
                return this.products.some((product) => {
                    return !this.isInStock(product);
                });
            },
            allProductsOutOfStock() {
                if (this.products.length < 1) {
                    return true;
                }
                return this.products.every((product) => {
                    return !this.isInStock(product);
                });
            },
            productRestrictions() {
                const defaultRestrictions = [...Array(this.maxProductsCount)];
                const restrictions = _.get(this.campaign, 'compensation_settings.product_restrictions', []);
                return restrictions.length > 0 ? restrictions : defaultRestrictions;
            },
            renderCardTypes() {
                const baseProductsList =
                    this.selectProducts && !this.useCheckoutUrl && this.maxProductsCount > 0
                        ? this.productRestrictions.map(() => {
                              return PRODUCT_CARD_TYPE_RESTRICTION;
                          })
                        : [];

                return this.products.reduce((result, restriction, index) => {
                    result[index] = PRODUCT_CARD_TYPE_SELECTED;
                    return result;
                }, baseProductsList);
            },
            validationError() {
                if (_.isArray(this.errors)) {
                    return _.get(this, 'errors[0][0]');
                } else {
                    return _.get(_.values(this.errors), '[0][0]');
                }
            },
            checkoutError() {
                return this.checkOutUrlErrorMessage ? [this.checkOutUrlErrorMessage] : null;
            },
            error() {
                if (this.validationError) {
                    return this.validationError;
                }

                if (this.errorMessage) {
                    return this.errorMessage;
                }
            },
            payload() {
                return {
                    products: this.products
                        .filter((product) => {
                            return !!product && !this.isHidden(product);
                        })
                        .map((product) => {
                            const item = {
                                id: product.id
                            };

                            if (product.variant) {
                                item.options = product.variant.option_values.map((value) => {
                                    return {
                                        id: value.option_id,
                                        value_id: value.id
                                    };
                                });
                            }

                            return item;
                        })
                };
            },
            isCompleted() {
                return _.get(this, 'proposalStep.is_complete', false);
            },
            customOutOfStockMessage() {
                return _.get(this.campaign, 'customization.products_out_of_stock_message', null);
            },
            hasProductsWithOptions() {
                return this.products.reduce((result, product) => {
                    if (product.select_options === true) {
                        result = true;
                    }

                    return result;
                }, false);
            }
        },
        methods: {
            handleSelectProductClick(restriction = '', index) {
                const restrictionString = encodeURIComponent(JSON.stringify(restriction));
                this.$router.push({
                    name: 'v2.productSelection.products',
                    query: {
                        restriction: restrictionString,
                        productIndex: index
                    }
                });
                this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_PRODUCT_SELECTION_START_EVENT);
            },
            handleEditProductClick(product, index, variantId) {
                this.$router.push({
                    name: 'v2.productSelection.product',
                    params: { product_id: product.id },
                    query: {
                        productIndex: index,
                        variantId
                    }
                });
            },
            handleCheckoutUrl() {
                this.$emit('checkout-products', { payload: { url: this.checkoutUrl } });
            },
            handleShopifyProductUrl() {
                this.$emit('load-product-url', this.checkoutUrl);
            },
            handleRemoveProduct(index) {
                this.$emit('remove-product', index);
            },
            isInStock(product) {
                if (!product.variant) {
                    return !!product.is_in_stock;
                }

                return product.variant.is_in_stock !== false;
            },
            isHidden(product) {
                // Todo: remove from products. Don't just hide.
                return this.shouldHandleOutOfStock && !this.isInStock(product);
            }
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom {
        margin: 0 0 $g-spacing-5 0;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .example-link {
        margin-top: $g-spacing-5 !important;

        & div {
            font-size: $g-spacing-4;
        }

        & div:last-child {
            font-style: italic;
        }
    }

    .error {
        color: $g-font-color-error;
    }

    .products-out-of-stock {
        padding: $g-spacing-8;
        margin-top: 8rem;
    }

    .break-new-line {
        white-space: pre-line;
    }
</style>
